/**
 * Bookmark Category Keywords & Detection System
 *
 * Auto-detects bookmark categories based on URL, title, and description
 * using i18n keywords in 5 languages (DE, EN, FR, ES, IT)
 */

// Category Keys
export const CATEGORY_KEYS = {
  WORK: 'WORK',
  DEV: 'DEV',
  AI: 'AI',
  DESIGN: 'DESIGN',
  TOOLS: 'TOOLS',
  NEWS: 'NEWS',
  SHOPPING: 'SHOPPING',
  DEALS: 'DEALS',
  FINANCE: 'FINANCE',
  FOOD: 'FOOD',
  SNACKS: 'SNACKS',
  MEALS: 'MEALS',
  BAKING: 'BAKING',
  DESSERT: 'DESSERT',
  TRAVEL: 'TRAVEL',
  FITNESS: 'FITNESS',
  ENTERTAINMENT: 'ENTERTAINMENT',
  GAMING: 'GAMING',
  DIY: 'DIY',
  INBOX: 'INBOX',
  IMPORTANT: 'IMPORTANT',
  RANDOM: 'RANDOM',
  OTHER: 'OTHER'
};

/**
 * Category definitions with:
 * - keywords: Multi-language keywords (DE/EN/FR/ES/IT)
 * - urlPatterns: Domain/path patterns for URL matching
 * - priority: Higher = more specific (checked first)
 */
export const categoryDefinitions = {
  [CATEGORY_KEYS.DEV]: {
    priority: 100,
    keywords: [
      // EN
      'developer', 'development', 'programming', 'code', 'coding', 'software', 'api', 'sdk',
      'frontend', 'backend', 'fullstack', 'devops', 'repository', 'commit', 'branch', 'merge',
      // DE
      'entwicklung', 'entwickler', 'programmierung', 'programmieren', 'softwareentwicklung',
      // FR
      'développement', 'développeur', 'programmation', 'logiciel',
      // ES
      'desarrollo', 'desarrollador', 'programación', 'programador',
      // IT
      'sviluppo', 'sviluppatore', 'programmazione'
    ],
    urlPatterns: [
      'github.com', 'gitlab.com', 'bitbucket.org', 'stackoverflow.com', 'stackexchange.com',
      'npmjs.com', 'pypi.org', 'maven', 'gradle', 'docker.com', 'kubernetes.io',
      'reactjs.org', 'vuejs.org', 'angular.io', 'nodejs.org', 'spring.io',
      'developer.mozilla.org', 'mdn', 'w3schools.com', 'codepen.io', 'jsfiddle.net',
      'vercel.com', 'netlify.com', 'heroku.com', 'digitalocean.com',
      'jetbrains.com', 'visualstudio.com', 'vscode', 'intellij',
      'localhost', 'jenkins', '127.0.0.1'
    ]
  },

  [CATEGORY_KEYS.AI]: {
    priority: 95,
    keywords: [
      // EN
      'artificial intelligence', 'machine learning', 'deep learning', 'neural network',
      'chatbot', 'language model', 'generative', 'transformer', 'gpt', 'llm',
      // DE
      'künstliche intelligenz', 'maschinelles lernen', 'neuronales netz', 'sprachmodell',
      // FR
      'intelligence artificielle', 'apprentissage automatique', 'réseau neuronal',
      // ES
      'inteligencia artificial', 'aprendizaje automático', 'red neuronal',
      // IT
      'intelligenza artificiale', 'apprendimento automatico', 'rete neurale',
      // Short forms
      'ki', 'ai', 'ml', 'nlp', 'prompt', 'claude'
    ],
    urlPatterns: [
      'openai.com', 'anthropic.com', 'claude.ai', 'claude', 'chat.openai.com', 'chatgpt',
      'huggingface.co', 'tensorflow.org', 'pytorch.org', 'keras.io',
      'midjourney', 'stability.ai', 'replicate.com', 'cohere.com',
      'perplexity.ai', 'gemini.google', 'bard.google', 'copilot'
    ]
  },

  [CATEGORY_KEYS.DESIGN]: {
    priority: 90,
    keywords: [
      // EN
      'design', 'designer', 'graphic', 'visual', 'ui', 'ux', 'user interface', 'user experience',
      'typography', 'color palette', 'mockup', 'wireframe', 'prototype', 'illustration',
      // DE
      'gestaltung', 'grafik', 'benutzeroberfläche', 'schriftart', 'farbpalette',
      // FR
      'conception', 'graphique', 'interface utilisateur', 'typographie',
      // ES
      'diseño', 'gráfico', 'interfaz de usuario', 'tipografía',
      // IT
      'progettazione', 'grafica', 'interfaccia utente'
    ],
    urlPatterns: [
      'figma.com', 'sketch.com', 'adobe.com', 'canva.com', 'dribbble.com', 'behance.net',
      'invisionapp.com', 'zeplin.io', 'framer.com', 'webflow.com',
      'fontawesome.com', 'fonts.google.com', 'coolors.co', 'colorhunt.co',
      'unsplash.com', 'pexels.com', 'freepik.com', 'flaticon.com'
    ]
  },

  [CATEGORY_KEYS.WORK]: {
    priority: 85,
    keywords: [
      // EN
      'work', 'office', 'business', 'project', 'meeting', 'conference', 'presentation',
      'document', 'spreadsheet', 'collaboration', 'team', 'workflow', 'productivity',
      // DE
      'arbeit', 'büro', 'geschäft', 'projekt', 'besprechung', 'konferenz', 'präsentation',
      'dokument', 'tabelle', 'zusammenarbeit', 'produktivität',
      // FR
      'travail', 'bureau', 'affaires', 'projet', 'réunion', 'conférence', 'présentation',
      // ES
      'trabajo', 'oficina', 'negocio', 'proyecto', 'reunión', 'conferencia', 'presentación',
      // IT
      'lavoro', 'ufficio', 'affari', 'progetto', 'riunione', 'conferenza', 'presentazione'
    ],
    urlPatterns: [
      'notion.so', 'trello.com', 'asana.com', 'monday.com', 'clickup.com',
      'jira', 'confluence', 'atlassian.com', 'basecamp.com',
      'slack.com', 'teams.microsoft.com', 'zoom.us', 'meet.google.com',
      'docs.google.com', 'sheets.google.com', 'slides.google.com',
      'office.com', 'microsoft365', 'dropbox.com', 'box.com'
    ]
  },

  [CATEGORY_KEYS.FINANCE]: {
    priority: 80,
    keywords: [
      // EN
      'finance', 'financial', 'bank', 'banking', 'money', 'investment', 'invest', 'stock',
      'trading', 'crypto', 'cryptocurrency', 'bitcoin', 'ethereum', 'portfolio', 'dividend',
      // DE
      'finanzen', 'finanziell', 'geld', 'investition', 'investieren', 'aktie', 'aktien',
      'handel', 'krypto', 'kryptowährung', 'depot', 'dividende', 'sparplan', 'etf',
      // FR
      'finances', 'financier', 'banque', 'argent', 'investissement', 'action', 'bourse',
      // ES
      'finanzas', 'financiero', 'banco', 'dinero', 'inversión', 'acción', 'bolsa',
      // IT
      'finanza', 'finanziario', 'banca', 'soldi', 'investimento', 'azione', 'borsa'
    ],
    urlPatterns: [
      'consorsbank', 'comdirect', 'ing.de', 'dkb.de', 'sparkasse', 'volksbank',
      'tradingview.com', 'yahoo.com/finance', 'finance.yahoo', 'bloomberg.com',
      'coinbase.com', 'binance.com', 'kraken.com', 'etoro.com',
      'paypal.com', 'wise.com', 'revolut.com', 'n26.com',
      'finanzen.net', 'onvista.de', 'extraetf.com', 'justetf.com'
    ]
  },

  [CATEGORY_KEYS.SHOPPING]: {
    priority: 75,
    keywords: [
      // EN
      'shop', 'shopping', 'store', 'buy', 'purchase', 'order', 'cart', 'checkout',
      'product', 'price', 'sale', 'marketplace',
      // DE
      'einkaufen', 'kaufen', 'bestellen', 'warenkorb', 'kasse', 'produkt', 'preis',
      'marktplatz', 'laden', 'geschäft',
      // FR
      'acheter', 'achat', 'magasin', 'boutique', 'panier', 'commande', 'prix',
      // ES
      'comprar', 'compra', 'tienda', 'carrito', 'pedido', 'precio',
      // IT
      'comprare', 'acquisto', 'negozio', 'carrello', 'ordine', 'prezzo'
    ],
    urlPatterns: [
      'amazon.', 'ebay.', 'etsy.com', 'aliexpress.com', 'wish.com',
      'zalando.', 'aboutyou.', 'asos.com', 'hm.com', 'zara.com',
      'mediamarkt.', 'saturn.', 'otto.de', 'idealo.de', 'geizhals.',
      'ikea.com', 'wayfair.com', 'homedepot.com'
    ]
  },

  [CATEGORY_KEYS.DEALS]: {
    priority: 78,
    keywords: [
      // EN
      'deal', 'deals', 'discount', 'coupon', 'voucher', 'promo', 'promotion', 'offer',
      'bargain', 'sale', 'clearance', 'black friday', 'cyber monday',
      // DE
      'rabatt', 'rabatte', 'angebot', 'angebote', 'gutschein', 'aktion', 'schnäppchen',
      'reduziert', 'prozent', 'sparen',
      // FR
      'réduction', 'remise', 'promotion', 'offre', 'soldes', 'bon plan',
      // ES
      'descuento', 'oferta', 'cupón', 'promoción', 'rebajas', 'ganga',
      // IT
      'sconto', 'offerta', 'coupon', 'promozione', 'saldi', 'affare'
    ],
    urlPatterns: [
      'mydealz.de', 'slickdeals.net', 'hotukdeals.com', 'dealabs.com',
      'groupon.', 'retailmenot.com', 'coupons.com', 'honey.',
      'cashback', 'shoop.de', 'igraal.'
    ]
  },

  [CATEGORY_KEYS.FOOD]: {
    priority: 70,
    keywords: [
      // EN
      'food', 'recipe', 'recipes', 'cooking', 'cook', 'kitchen', 'meal', 'dish',
      'ingredient', 'ingredients', 'cuisine', 'restaurant', 'chef', 'baking', 'bake',
      // DE
      'essen', 'rezept', 'rezepte', 'kochen', 'küche', 'mahlzeit', 'gericht',
      'zutat', 'zutaten', 'backen', 'restaurant',
      // FR
      'nourriture', 'recette', 'recettes', 'cuisine', 'cuisiner', 'repas', 'plat',
      'ingrédient', 'pâtisserie',
      // ES
      'comida', 'receta', 'recetas', 'cocina', 'cocinar', 'plato', 'ingrediente',
      // IT
      'cibo', 'ricetta', 'ricette', 'cucina', 'cucinare', 'piatto', 'ingrediente'
    ],
    urlPatterns: [
      'chefkoch.de', 'allrecipes.com', 'foodnetwork.com', 'epicurious.com',
      'seriouseats.com', 'bonappetit.com', 'tasty.co', 'delish.com',
      'hellofresh.', 'marleyspoon.', 'lieferando.', 'ubereats.',
      'yelp.com', 'tripadvisor.com/restaurant'
    ]
  },

  [CATEGORY_KEYS.TRAVEL]: {
    priority: 70,
    keywords: [
      // EN
      'travel', 'trip', 'vacation', 'holiday', 'flight', 'hotel', 'booking', 'destination',
      'tourism', 'tourist', 'airport', 'airline', 'cruise',
      // DE
      'reise', 'reisen', 'urlaub', 'flug', 'flüge', 'buchung', 'ziel', 'tourismus',
      'flughafen', 'kreuzfahrt',
      // FR
      'voyage', 'voyager', 'vacances', 'vol', 'hôtel', 'réservation', 'destination',
      // ES
      'viaje', 'viajar', 'vacaciones', 'vuelo', 'hotel', 'reserva', 'destino',
      // IT
      'viaggio', 'viaggiare', 'vacanza', 'volo', 'albergo', 'prenotazione', 'destinazione'
    ],
    urlPatterns: [
      'booking.com', 'airbnb.', 'expedia.', 'tripadvisor.com', 'hotels.com',
      'skyscanner.', 'kayak.', 'google.com/travel', 'google.com/flights',
      'lufthansa.com', 'ryanair.com', 'easyjet.com', 'eurowings.',
      'rome2rio.com', 'flixbus.', 'bahn.de', 'trainline.'
    ]
  },

  [CATEGORY_KEYS.FITNESS]: {
    priority: 65,
    keywords: [
      // EN
      'fitness', 'workout', 'exercise', 'gym', 'training', 'sport', 'sports', 'health',
      'yoga', 'running', 'cycling', 'weight', 'muscle', 'cardio',
      // DE
      'fitness', 'training', 'sport', 'gesundheit', 'übung', 'übungen', 'laufen',
      'radfahren', 'gewicht', 'muskel',
      // FR
      'fitness', 'entraînement', 'sport', 'santé', 'exercice', 'musculation',
      // ES
      'fitness', 'entrenamiento', 'deporte', 'salud', 'ejercicio', 'gimnasio',
      // IT
      'fitness', 'allenamento', 'sport', 'salute', 'esercizio', 'palestra'
    ],
    urlPatterns: [
      'strava.com', 'myfitnesspal.com', 'fitbit.com', 'garmin.com/fitness',
      'nike.com/training', 'adidas.com/training', 'peloton.com',
      'bodybuilding.com', 'muscleandstrength.com', 'darebee.com'
    ]
  },

  [CATEGORY_KEYS.ENTERTAINMENT]: {
    priority: 60,
    keywords: [
      // EN
      'entertainment', 'movie', 'movies', 'film', 'films', 'series', 'tv', 'television',
      'streaming', 'watch', 'video', 'show', 'episode', 'season', 'music', 'podcast',
      // DE
      'unterhaltung', 'film', 'filme', 'serie', 'serien', 'fernsehen', 'schauen',
      'folge', 'staffel', 'musik',
      // FR
      'divertissement', 'film', 'films', 'série', 'séries', 'télévision', 'musique',
      // ES
      'entretenimiento', 'película', 'películas', 'serie', 'series', 'televisión', 'música',
      // IT
      'intrattenimento', 'film', 'serie', 'televisione', 'musica'
    ],
    urlPatterns: [
      'netflix.com', 'primevideo.com', 'amazon.com/prime-video', 'disneyplus.com',
      'hulu.com', 'hbomax.com', 'max.com', 'peacocktv.com', 'paramountplus.com',
      'youtube.com', 'vimeo.com', 'twitch.tv', 'dailymotion.com',
      'spotify.com', 'apple.com/music', 'soundcloud.com', 'deezer.com',
      'imdb.com', 'rottentomatoes.com', 'letterboxd.com'
    ]
  },

  [CATEGORY_KEYS.GAMING]: {
    priority: 65,
    keywords: [
      // EN
      'game', 'games', 'gaming', 'gamer', 'play', 'player', 'esports', 'console',
      'pc gaming', 'multiplayer', 'rpg', 'fps', 'mmorpg',
      // DE
      'spiel', 'spiele', 'spielen', 'spieler', 'konsole',
      // FR
      'jeu', 'jeux', 'jouer', 'joueur', 'console',
      // ES
      'juego', 'juegos', 'jugar', 'jugador', 'consola',
      // IT
      'gioco', 'giochi', 'giocare', 'giocatore', 'console'
    ],
    urlPatterns: [
      'steam', 'steampowered.com', 'epicgames.com', 'gog.com', 'origin.com',
      'xbox.com', 'playstation.com', 'nintendo.com', 'ubisoft.com',
      'ign.com', 'gamespot.com', 'kotaku.com', 'polygon.com',
      'twitch.tv', 'discord.com', 'reddit.com/r/gaming'
    ]
  },

  [CATEGORY_KEYS.NEWS]: {
    priority: 55,
    keywords: [
      // EN
      'news', 'breaking', 'headline', 'headlines', 'article', 'journalism', 'reporter',
      'press', 'media', 'current events', 'politics', 'world news',
      // DE
      'nachrichten', 'neuigkeiten', 'schlagzeile', 'schlagzeilen', 'artikel', 'presse',
      'medien', 'politik', 'aktuell',
      // FR
      'actualités', 'nouvelles', 'informations', 'presse', 'médias', 'politique',
      // ES
      'noticias', 'actualidad', 'prensa', 'medios', 'política',
      // IT
      'notizie', 'attualità', 'stampa', 'media', 'politica'
    ],
    urlPatterns: [
      'bbc.com', 'cnn.com', 'reuters.com', 'apnews.com', 'nytimes.com',
      'theguardian.com', 'washingtonpost.com', 'wsj.com',
      'spiegel.de', 'zeit.de', 'faz.net', 'sueddeutsche.de', 'tagesschau.de',
      'lemonde.fr', 'lefigaro.fr', 'elpais.com', 'repubblica.it',
      'news.google.com', 'news.ycombinator.com', 'reddit.com/r/news'
    ]
  },

  [CATEGORY_KEYS.TOOLS]: {
    priority: 50,
    keywords: [
      // EN
      'tool', 'tools', 'utility', 'utilities', 'converter', 'calculator', 'generator',
      'online tool', 'free tool', 'web tool',
      // DE
      'werkzeug', 'werkzeuge', 'hilfsmittel', 'umrechner', 'rechner', 'generator',
      // FR
      'outil', 'outils', 'utilitaire', 'convertisseur', 'calculateur', 'générateur',
      // ES
      'herramienta', 'herramientas', 'utilidad', 'convertidor', 'calculadora', 'generador',
      // IT
      'strumento', 'strumenti', 'utilità', 'convertitore', 'calcolatore', 'generatore'
    ],
    urlPatterns: [
      'smallpdf.com', 'ilovepdf.com', 'pdf', 'tinypng.com', 'remove.bg',
      'regex101.com', 'jsonformatter', 'base64', 'urlencoder',
      'speedtest.net', 'fast.com', 'whatismyip',
      'wolframalpha.com', 'desmos.com'
    ]
  },

  [CATEGORY_KEYS.DIY]: {
    priority: 50,
    keywords: [
      // EN
      'diy', 'do it yourself', 'craft', 'crafts', 'handmade', 'homemade', 'tutorial',
      'how to', 'howto', 'guide', 'maker', 'making',
      // DE
      'selbermachen', 'basteln', 'handgemacht', 'anleitung', 'heimwerken', 'heimwerker',
      // FR
      'bricolage', 'fait maison', 'artisanat', 'tutoriel', 'guide',
      // ES
      'manualidades', 'hecho a mano', 'casero', 'tutorial', 'guía',
      // IT
      'fai da te', 'fatto a mano', 'artigianato', 'tutorial', 'guida'
    ],
    urlPatterns: [
      'instructables.com', 'makezine.com', 'hackaday.com', 'diy.com',
      'pinterest.com/diy', 'youtube.com/diy', 'wikihow.com'
    ]
  },

  [CATEGORY_KEYS.IMPORTANT]: {
    priority: 45,
    keywords: [
      // EN
      'important', 'urgent', 'priority', 'critical', 'asap', 'deadline', 'must',
      // DE
      'wichtig', 'dringend', 'priorität', 'kritisch', 'deadline', 'muss',
      // FR
      'important', 'urgent', 'priorité', 'critique',
      // ES
      'importante', 'urgente', 'prioridad', 'crítico',
      // IT
      'importante', 'urgente', 'priorità', 'critico'
    ],
    urlPatterns: []
  },

  [CATEGORY_KEYS.INBOX]: {
    priority: 40,
    keywords: [
      // EN
      'inbox', 'later', 'read later', 'save for later', 'to read', 'bookmark', 'saved',
      // DE
      'später', 'später lesen', 'zum lesen', 'gespeichert', 'lesezeichen',
      // FR
      'plus tard', 'lire plus tard', 'à lire', 'sauvegardé',
      // ES
      'más tarde', 'leer después', 'para leer', 'guardado',
      // IT
      'più tardi', 'leggere dopo', 'da leggere', 'salvato'
    ],
    urlPatterns: [
      'pocket.com', 'getpocket.com', 'instapaper.com', 'raindrop.io', 'pinboard.in'
    ]
  },

  [CATEGORY_KEYS.RANDOM]: {
    priority: 30,
    keywords: [
      // EN
      'random', 'misc', 'miscellaneous', 'other', 'stuff', 'various', 'wtf', 'fun', 'funny',
      // DE
      'zufällig', 'sonstiges', 'verschiedenes', 'anderes', 'zeug', 'witzig', 'lustig',
      // FR
      'aléatoire', 'divers', 'autre', 'drôle', 'marrant',
      // ES
      'aleatorio', 'varios', 'otro', 'gracioso', 'divertido',
      // IT
      'casuale', 'vario', 'altro', 'divertente'
    ],
    urlPatterns: [
      'reddit.com', '9gag.com', 'imgur.com', 'giphy.com'
    ]
  },

  [CATEGORY_KEYS.OTHER]: {
    priority: 0,
    keywords: [],
    urlPatterns: []
  }
};

/**
 * Normalize text for matching:
 * - lowercase
 * - remove diacritics (ä→a, é→e, etc.)
 * - remove punctuation
 * - trim whitespace
 */
export const normalizeText = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^\w\s]/g, ' ')        // Replace punctuation with space
    .replace(/\s+/g, ' ')            // Collapse multiple spaces
    .trim();
};

/**
 * Extract searchable parts from URL
 */
export const extractUrlParts = (url) => {
  if (!url) return '';
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    const parts = [
      urlObj.hostname.replace('www.', ''),
      urlObj.pathname,
      urlObj.search
    ].join(' ');
    return normalizeText(parts);
  } catch {
    return normalizeText(url);
  }
};

/**
 * Check if text contains any of the patterns
 */
const matchesPatterns = (text, patterns) => {
  if (!text || !patterns || patterns.length === 0) return false;
  const normalizedText = normalizeText(text);
  return patterns.some(pattern => normalizedText.includes(normalizeText(pattern)));
};

/**
 * Check if text contains any of the keywords
 */
const matchesKeywords = (text, keywords) => {
  if (!text || !keywords || keywords.length === 0) return false;
  const normalizedText = normalizeText(text);
  const words = normalizedText.split(' ');

  return keywords.some(keyword => {
    const normalizedKeyword = normalizeText(keyword);
    // Check for exact word match or phrase match
    if (normalizedKeyword.includes(' ')) {
      // Multi-word keyword: check if phrase exists
      return normalizedText.includes(normalizedKeyword);
    } else {
      // Single word: check for word match (not just substring)
      return words.some(word => word === normalizedKeyword || word.startsWith(normalizedKeyword));
    }
  });
};

/**
 * Detect bookmark category based on URL, title, and description
 *
 * @param {Object} bookmark - Bookmark object with url, title, description
 * @returns {string} CategoryKey
 */
export const detectBookmarkCategory = ({ url, title, description, notes }) => {
  // If bookmark already has a user-defined category, respect it
  // (handled in the component, not here)

  const urlText = extractUrlParts(url);
  const titleText = normalizeText(title);
  const descText = normalizeText(description || notes || '');

  // Sort categories by priority (highest first)
  const sortedCategories = Object.entries(categoryDefinitions)
    .filter(([key]) => key !== CATEGORY_KEYS.OTHER)
    .sort(([, a], [, b]) => b.priority - a.priority);

  // Check each category in priority order
  for (const [categoryKey, definition] of sortedCategories) {
    const { keywords, urlPatterns } = definition;

    // 1. Check URL patterns first (most reliable)
    if (matchesPatterns(urlText, urlPatterns)) {
      return categoryKey;
    }

    // 2. Check URL for keywords
    if (matchesKeywords(urlText, keywords)) {
      return categoryKey;
    }

    // 3. Check title for keywords
    if (matchesKeywords(titleText, keywords)) {
      return categoryKey;
    }

    // 4. Check description for keywords
    if (matchesKeywords(descText, keywords)) {
      return categoryKey;
    }
  }

  // Fallback
  return CATEGORY_KEYS.OTHER;
};

/**
 * Get category from bookmark, using user-defined category if available,
 * otherwise auto-detect
 */
export const getBookmarkCategory = (bookmark) => {
  // FIRST: Check strong URL patterns (YouTube, GitHub, etc.)
  // These always take priority for icon assignment
  if (bookmark.url) {
    const urlLower = bookmark.url.toLowerCase();
    const strongUrlPatterns = [
      { pattern: 'youtube.com', category: CATEGORY_KEYS.ENTERTAINMENT },
      { pattern: 'youtu.be', category: CATEGORY_KEYS.ENTERTAINMENT },
      { pattern: 'github.com', category: CATEGORY_KEYS.DEV },
      { pattern: 'gitlab.com', category: CATEGORY_KEYS.DEV },
      { pattern: 'stackoverflow.com', category: CATEGORY_KEYS.DEV },
      { pattern: 'netflix.com', category: CATEGORY_KEYS.ENTERTAINMENT },
      { pattern: 'twitch.tv', category: CATEGORY_KEYS.ENTERTAINMENT },
      { pattern: 'spotify.com', category: CATEGORY_KEYS.ENTERTAINMENT },
      { pattern: 'vimeo.com', category: CATEGORY_KEYS.ENTERTAINMENT },
    ];
    for (const { pattern, category } of strongUrlPatterns) {
      if (urlLower.includes(pattern)) {
        return category;
      }
    }
  }

  // If user has set a category, try to map it to our keys
  if (bookmark.category) {
    // Remove "categories." prefix if present (legacy data format)
    let categoryName = bookmark.category;
    if (categoryName.startsWith('categories.')) {
      categoryName = categoryName.replace('categories.', '');
    }

    const normalizedCategory = categoryName.toUpperCase().replace(/\s+/g, '_');
    if (CATEGORY_KEYS[normalizedCategory]) {
      return normalizedCategory;
    }
    // Try to match common category names
    const categoryMappings = {
      'arbeit': CATEGORY_KEYS.WORK,
      'work': CATEGORY_KEYS.WORK,
      'entwicklung': CATEGORY_KEYS.DEV,
      'dev': CATEGORY_KEYS.DEV,
      'ki': CATEGORY_KEYS.AI,
      'ai': CATEGORY_KEYS.AI,
      'design': CATEGORY_KEYS.DESIGN,
      'tools': CATEGORY_KEYS.TOOLS,
      'news': CATEGORY_KEYS.NEWS,
      'nachrichten': CATEGORY_KEYS.NEWS,
      'shopping': CATEGORY_KEYS.SHOPPING,
      'einkaufen': CATEGORY_KEYS.SHOPPING,
      'deals': CATEGORY_KEYS.DEALS,
      'angebote': CATEGORY_KEYS.DEALS,
      'finanzen': CATEGORY_KEYS.FINANCE,
      'finance': CATEGORY_KEYS.FINANCE,
      'münzen': CATEGORY_KEYS.FINANCE,
      'münzen sammlung': CATEGORY_KEYS.FINANCE,
      'münzsammlung': CATEGORY_KEYS.FINANCE,
      'coins': CATEGORY_KEYS.FINANCE,
      'coin collection': CATEGORY_KEYS.FINANCE,
      'geld': CATEGORY_KEYS.FINANCE,
      'money': CATEGORY_KEYS.FINANCE,
      'investment': CATEGORY_KEYS.FINANCE,
      'investition': CATEGORY_KEYS.FINANCE,
      'essen': CATEGORY_KEYS.FOOD,
      'food': CATEGORY_KEYS.FOOD,
      'rezepte': CATEGORY_KEYS.FOOD,
      'kochen': CATEGORY_KEYS.FOOD,
      'cooking': CATEGORY_KEYS.FOOD,
      'backen': CATEGORY_KEYS.BAKING,
      'baking': CATEGORY_KEYS.BAKING,
      'kuchen': CATEGORY_KEYS.BAKING,
      'torten': CATEGORY_KEYS.BAKING,
      'torte': CATEGORY_KEYS.BAKING,
      'cake': CATEGORY_KEYS.BAKING,
      'cakes': CATEGORY_KEYS.BAKING,
      'backwaren': CATEGORY_KEYS.BAKING,
      'gebäck': CATEGORY_KEYS.BAKING,
      'pastry': CATEGORY_KEYS.BAKING,
      'süßes': CATEGORY_KEYS.DESSERT,
      'süßes nachspeisen': CATEGORY_KEYS.DESSERT,
      'nachspeisen': CATEGORY_KEYS.DESSERT,
      'nachspeise': CATEGORY_KEYS.DESSERT,
      'desserts': CATEGORY_KEYS.DESSERT,
      'dessert': CATEGORY_KEYS.DESSERT,
      'nachtisch': CATEGORY_KEYS.DESSERT,
      'sweets': CATEGORY_KEYS.DESSERT,
      'snacks': CATEGORY_KEYS.SNACKS,
      'snack': CATEGORY_KEYS.SNACKS,
      'warme mahlzeiten': CATEGORY_KEYS.MEALS,
      'mahlzeiten': CATEGORY_KEYS.MEALS,
      'meals': CATEGORY_KEYS.MEALS,
      'hauptgerichte': CATEGORY_KEYS.MEALS,
      'main dishes': CATEGORY_KEYS.MEALS,
      'reisen': CATEGORY_KEYS.TRAVEL,
      'travel': CATEGORY_KEYS.TRAVEL,
      'urlaub': CATEGORY_KEYS.TRAVEL,
      'ausflug': CATEGORY_KEYS.TRAVEL,
      'ausflüge': CATEGORY_KEYS.TRAVEL,
      'geplante ausflüge': CATEGORY_KEYS.TRAVEL,
      'planned trips': CATEGORY_KEYS.TRAVEL,
      'trips': CATEGORY_KEYS.TRAVEL,
      'excursions': CATEGORY_KEYS.TRAVEL,
      'vacations': CATEGORY_KEYS.TRAVEL,
      'fitness': CATEGORY_KEYS.FITNESS,
      'sport': CATEGORY_KEYS.FITNESS,
      'unterhaltung': CATEGORY_KEYS.ENTERTAINMENT,
      'entertainment': CATEGORY_KEYS.ENTERTAINMENT,
      'youtube': CATEGORY_KEYS.ENTERTAINMENT,
      'video': CATEGORY_KEYS.ENTERTAINMENT,
      'videos': CATEGORY_KEYS.ENTERTAINMENT,
      'musik': CATEGORY_KEYS.ENTERTAINMENT,
      'music': CATEGORY_KEYS.ENTERTAINMENT,
      'filme': CATEGORY_KEYS.ENTERTAINMENT,
      'movies': CATEGORY_KEYS.ENTERTAINMENT,
      'serien': CATEGORY_KEYS.ENTERTAINMENT,
      'streaming': CATEGORY_KEYS.ENTERTAINMENT,
      'gaming': CATEGORY_KEYS.GAMING,
      'spiele': CATEGORY_KEYS.GAMING,
      'diy': CATEGORY_KEYS.DIY,
      'wichtig': CATEGORY_KEYS.IMPORTANT,
      'important': CATEGORY_KEYS.IMPORTANT,
      'später': CATEGORY_KEYS.INBOX,
      'inbox': CATEGORY_KEYS.INBOX,
      'sonstiges': CATEGORY_KEYS.OTHER,
      'other': CATEGORY_KEYS.OTHER
    };
    const mapped = categoryMappings[categoryName.toLowerCase()];
    if (mapped) return mapped;

    // Keyword-based fallback search for partial matches
    // Check if any mapping keyword is contained in the category name
    const lowerCategoryName = categoryName.toLowerCase();
    for (const [keyword, categoryKey] of Object.entries(categoryMappings)) {
      // Check if keyword is contained in category name (e.g. "münzen" in "münzen sammlung")
      if (lowerCategoryName.includes(keyword) || keyword.includes(lowerCategoryName)) {
        return categoryKey;
      }
    }

    // General URL-based fallback for other patterns
    if (bookmark.url) {
      const urlBasedCategory = detectBookmarkCategory(bookmark);
      if (urlBasedCategory && urlBasedCategory !== CATEGORY_KEYS.OTHER) {
        return urlBasedCategory;
      }
    }

    // User-defined category that doesn't map to a key - return as-is
    // This preserves custom categories like "Bücher", "Meine Links", etc.
    return categoryName;
  }

  // No user category - auto-detect
  return detectBookmarkCategory(bookmark);
};

/**
 * Strip "categories." prefix from category names (legacy data format)
 */
export const stripCategoryPrefix = (categoryName) => {
  if (!categoryName || typeof categoryName !== 'string') return categoryName;
  if (categoryName.startsWith('categories.')) {
    return categoryName.replace('categories.', '');
  }
  return categoryName;
};

export default {
  CATEGORY_KEYS,
  categoryDefinitions,
  detectBookmarkCategory,
  getBookmarkCategory,
  normalizeText,
  stripCategoryPrefix
};
